import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const images = [
  {
    src: 'https://images.unsplash.com/photo-1603367563698-67012943fd67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMGZhbWlseSUyMG91dGRvb3IlMjBuYXR1cmV8ZW58MHx8fHwxNzgxMzI2OTkzfDA&ixlib=rb-4.1.0&q=85',
    caption: 'Family afternoons',
    span: 'md:col-span-6 md:row-span-2',
  },
  {
    src: 'https://images.unsplash.com/photo-1533037932350-a08bba6019f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxmaXNoaW5nJTIwcG9uZCUyMG5hdHVyZXxlbnwwfHx8fDE3ODEzMjY5OTN8MA&ixlib=rb-4.1.0&q=85',
    caption: 'Golden-hour casts',
    span: 'md:col-span-3',
  },
  {
    src: 'https://images.pexels.com/photos/8491086/pexels-photo-8491086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    caption: 'BBQ tambayan',
    span: 'md:col-span-3',
  },
  {
    src: 'https://images.unsplash.com/photo-1603367563698-67012943fd67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMGZhbWlseSUyMG91dGRvb3IlMjBuYXR1cmV8ZW58MHx8fHwxNzgxMzI2OTkzfDA&ixlib=rb-4.1.0&q=85',
    caption: 'First catches',
    span: 'md:col-span-4',
  },
  {
    src: 'https://images.unsplash.com/photo-1776135933174-7465a18e98d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxiYW1ib28lMjBodXQlMjByZXNvcnQlMjBuYXR1cmV8ZW58MHx8fHwxNzgxMzI2OTkzfDA&ixlib=rb-4.1.0&q=85',
    caption: 'Quiet cabanas',
    span: 'md:col-span-4',
  },
  {
    src: 'https://images.unsplash.com/photo-1588676509970-63fe490c3712?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxmaXNoaW5nJTIwcG9uZCUyMG5hdHVyZXxlbnwwfHx8fDE3ODEzMjY5OTN8MA&ixlib=rb-4.1.0&q=85',
    caption: 'Pond mornings',
    span: 'md:col-span-4',
  },
];

export default function Gallery() {
  const [open, setOpen] = useState(null);
  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="overline text-muted mb-4">Mga sulyap</p>
            <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A look around <em className="italic font-light text-moss">the pond.</em>
            </h2>
          </div>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noreferrer"
            data-testid="gallery-instagram-link"
            className="overline text-pond hover:text-moss transition-colors inline-flex items-center gap-2"
          >
            Follow on Instagram <span>→</span>
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-5 md:auto-rows-[180px]">
          {images.map((img, i) => (
            <motion.button
              key={i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className={`group relative overflow-hidden rounded-2xl col-span-1 ${img.span} min-h-[180px]`}
            >
              <img
                src={img.src}
                alt={img.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-pond/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-3 left-3 right-3 text-offwhite text-left opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <p className="overline text-sky">{img.caption}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {open !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] bg-pond/95 flex items-center justify-center p-4 md:p-12"
          onClick={() => setOpen(null)}
        >
          <button
            onClick={() => setOpen(null)}
            className="absolute top-6 right-6 text-offwhite/80 hover:text-offwhite"
            aria-label="Close gallery"
            data-testid="gallery-close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={images[open].src}
            alt={images[open].caption}
            className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </section>
  );
}
